import type {
  AttentionItem,
  BusinessImpactData,
  CalendarImpactData,
  CompletedWorkflowRow,
  EarlyPattern,
  ImprovementOpportunity,
  MatureTodayData,
  PatientExperienceData,
  ReviewCall,
  SafetyCheck,
  TrialData,
  TrialReviewCall,
  WorkCompletedItem,
} from './types';

export const RCPT_TRIAL: TrialData = {
  day: 6,
  trialLength: 30,
  callsHandled: 47,
  completedByElva: 34,
  safelyEscalated: 9,
  needsAttention: 4,
  hoursSaved: 11.4,
  series: {
    calls: [3, 6, 5, 9, 11, 13],
    completed: [2, 4, 4, 7, 8, 9],
    escalated: [1, 1, 0, 2, 3, 2],
    attention: [0, 1, 1, 0, 1, 1],
  },
  liveNow: {
    isActive: true,
    patient: 'New patient · (415) 555-0182',
    intent: 'Booking intent · routine cleaning',
    duration: 134,
    transcript: [
      { who: 'patient', t: "Hi, I'd like to book a cleaning." },
      { who: 'elva', t: 'Of course. Are you a new patient with us?' },
      { who: 'patient', t: 'Yes, first time.' },
      { who: 'elva', t: 'Welcome. I can find you a slot — do mornings work?' },
      { who: 'patient', t: 'Mornings are great, after 9 ideally.' },
      { who: 'elva', t: 'I have Tuesday May 20 at 10am with Dr. Lin.' },
    ],
  },
};

export const RCPT_NEEDS_ATTENTION: AttentionItem[] = [
  {
    id: 'a1',
    patient: 'Carlos Reyes',
    source: 'Call',
    priority: 'urgent',
    summary: 'Clinical concern requires review',
    detail: 'Patient reported post-op bleeding after extraction',
    due: 'Now',
  },
  {
    id: 'a2',
    patient: 'Maria Garcia',
    source: 'Call',
    priority: 'high',
    summary: 'Patient requested a callback',
    detail: "Wants to confirm Wednesday's appointment after work",
    due: 'Today, 4:00 pm',
  },
  {
    id: 'a3',
    patient: 'Sandra Williams',
    source: 'SMS',
    priority: 'medium',
    summary: 'Insurance question needs staff confirmation',
    detail: 'MetLife plan coverage for crown not in our records',
    due: 'Today',
  },
  {
    id: 'a4',
    patient: 'Aaron Klein',
    source: 'Call',
    priority: 'medium',
    summary: 'Frustrated patient needs follow-up',
    detail: 'Felt rushed during last cleaning, wants to speak to office manager',
    due: 'Tomorrow',
  },
];

export const RCPT_CALLS_WORTH_REVIEWING: TrialReviewCall[] = [
  {
    id: 'r1',
    kind: 'Best completed',
    icon: 'check',
    accent: 'positive',
    title: 'New patient booked cleaning without staff help',
    detail: 'Marcia Hollis · Routine cleaning · Tue 2:30 pm with Dr. Lin',
    duration: '3:47',
  },
  {
    id: 'r2',
    kind: 'Safe escalation',
    icon: 'shield',
    accent: 'warning',
    title: 'Patient mentioned post-op bleeding, routed to team',
    detail: 'Carlos Reyes · Routed to Dr. Saito within 45 seconds',
    duration: '1:24',
  },
  {
    id: 'r3',
    kind: 'Knowledge gap',
    icon: 'book',
    accent: 'brass',
    title: 'Patient asked about Invisalign financing',
    detail: "Elva didn't have an answer — worth adding to your Brain",
    duration: '2:58',
  },
  {
    id: 'r4',
    kind: 'Patient requested human',
    icon: 'user',
    accent: 'neutral',
    title: 'Elva captured details and created callback',
    detail: 'Jane Park · Wanted to speak to Dr. Saito about treatment plan',
    duration: '1:12',
  },
];

export const RCPT_WORK_COMPLETED: WorkCompletedItem[] = [
  { id: 'w1', icon: 'calendar', label: 'Appointments booked', count: 14, sub: 'New patients, hygiene, consults' },
  { id: 'w2', icon: 'refresh', label: 'Reschedules completed', count: 8, sub: 'Moved without staff involvement' },
  { id: 'w3', icon: 'team', label: 'Patient details captured', count: 23, sub: 'Insurance, DOB, reason for visit' },
  { id: 'w4', icon: 'mail', label: 'Confirmations sent', count: 19, sub: 'SMS, email, forms links' },
  { id: 'w5', icon: 'stopwatch', label: 'After-hours captured', count: 7, sub: 'Calls handled when staff unavailable' },
];

export const RCPT_EARLY_PATTERNS: EarlyPattern[] = [
  { id: 'p1', text: 'Most appointment requests came between 8–10 am', count: 18 },
  { id: 'p2', text: 'Patients asked about insurance participation', count: 7 },
  { id: 'p3', text: 'Callers asked for same-day emergency availability', count: 4 },
  { id: 'p4', text: 'Patients were confused about forms before their appointment', count: 3 },
  { id: 'p5', text: 'Calls required clinical review', count: 2 },
];

export const RCPT_SAFETY_CHECKS: SafetyCheck[] = [
  { id: 's1', label: 'Clinical concerns escalated', count: 3, color: 'warning' },
  { id: 's2', label: 'Patient-requested-human honored', count: 4, color: 'positive' },
  { id: 's3', label: 'Unknown questions flagged', count: 5, color: 'brass' },
  { id: 's4', label: 'Sensitive requests avoided', count: 2, color: 'positive' },
];

export const RCPT_TRIAL_PROGRESS_INCLUDES = [
  'Calls handled',
  'Patient requests completed',
  'Staff time protected',
  'Safe escalations',
  'Appointments booked',
  'Calls reviewed',
  'Improvement opportunities',
];

export const RCPT_TODAY: MatureTodayData = {
  conversationsHandled: 64,
  completedWithoutStaff: 48,
  needsAttention: 6,
  appointmentsCreated: 18,
  staffTimeMin: 48 * 5,
  series: {
    conversations: [42, 51, 58, 47, 62, 55, 64],
    completed: [30, 38, 44, 35, 47, 41, 48],
    attention: [4, 3, 5, 2, 6, 4, 6],
    timeProtected: [2.5, 3.2, 3.7, 2.9, 3.9, 3.4, 4.0],
  },
};

export const RCPT_NEEDS_ATTENTION_FULL: AttentionItem[] = [
  {
    id: 'n1',
    patient: 'Carlos Reyes',
    source: 'Call',
    priority: 'urgent',
    summary: 'Clinical concern requires review',
    detail: 'Post-op bleeding after extraction',
    due: 'Now',
    assignedTo: 'Dr. Saito',
  },
  {
    id: 'n2',
    patient: 'Lana Williams',
    source: 'Call',
    priority: 'urgent',
    summary: 'Failed handoff to insurance team',
    detail: "Maria didn't pick up; queued for retry",
    due: 'Now',
    assignedTo: 'Maria L.',
  },
  {
    id: 'n3',
    patient: 'Theo Park',
    source: 'SMS',
    priority: 'urgent',
    summary: 'Frustrated patient needs follow-up',
    detail: 'Negative survey after Wednesday visit',
    due: 'Now',
    assignedTo: 'Sarah K.',
  },
  {
    id: 'n4',
    patient: 'Maria Garcia',
    source: 'Call',
    priority: 'high',
    summary: 'Patient requested a callback',
    detail: "Wants to confirm Wednesday's appointment",
    due: 'Today, 4:00 pm',
    assignedTo: 'Front desk',
  },
  {
    id: 'n5',
    patient: 'Sandra Williams',
    source: 'SMS',
    priority: 'medium',
    summary: 'Insurance question needs confirmation',
    detail: 'MetLife plan coverage for crown unclear',
    due: 'Today',
    assignedTo: 'Maria L.',
  },
  {
    id: 'n6',
    patient: 'Aaron Klein',
    source: 'Call',
    priority: 'medium',
    summary: 'Appointment conflict',
    detail: 'Patient booked overlapping slot last week',
    due: 'Tomorrow',
    assignedTo: 'Front desk',
  },
];

export const RCPT_COMPLETED_BY_ELVA: CompletedWorkflowRow[] = [
  { id: 'c1', icon: 'team', label: 'New patient appointments', count: 7, sub: 'Registered + scheduled' },
  { id: 'c2', icon: 'calendar', label: 'Hygiene appointments', count: 5, sub: 'Recall + first-time' },
  { id: 'c3', icon: 'bolt', label: 'Same-day emergencies', count: 2, sub: 'Routed to on-call dentist' },
  { id: 'c4', icon: 'refresh', label: 'Reschedules completed', count: 5, sub: 'Moved without staff' },
  { id: 'c5', icon: 'mail', label: 'Confirmations sent', count: 12, sub: 'SMS + email reminders' },
  { id: 'c6', icon: 'stopwatch', label: 'After-hours captured', count: 3, sub: 'Calls handled past 6 pm' },
  { id: 'c7', icon: 'list', label: 'Forms links sent', count: 4, sub: 'Patient intake & medical hx' },
  { id: 'c8', icon: 'shield-check', label: 'Insurance verifications', count: 6, sub: 'Confirmed coverage active' },
];

export const RCPT_MATURE_CALLS: ReviewCall[] = [
  {
    id: 'r1',
    kind: 'Best completed',
    icon: 'check',
    title: 'First-visit booking with insurance verification end-to-end',
    detail: 'Eleanor Whitfield · New patient · Hygiene Tue 2:30 pm',
    duration: '5:12',
  },
  {
    id: 'r2',
    kind: 'Safe escalation',
    icon: 'shield',
    title: 'Post-op concern routed to on-call dentist in 38 seconds',
    detail: 'Carlos Reyes · Routed to Dr. Saito',
    duration: '0:38',
  },
];

export const RCPT_IMPROVEMENT_OPPS: ImprovementOpportunity[] = [
  { id: 'o1', count: 12, observation: 'patients asked about Invisalign financing', action: 'brain', actionLabel: 'Add to Brain' },
  { id: 'o2', count: 9, observation: 'callers asked if you accept Delta Dental', action: 'brain', actionLabel: 'Add to Brain' },
  { id: 'o3', count: 6, observation: 'patients asked post-op medication questions', action: 'user', actionLabel: 'Doctor approval' },
  { id: 'o4', count: 5, observation: 'calls escalated because same-day rules were unclear', action: 'shield', actionLabel: 'Update rule' },
  { id: 'o5', count: 4, observation: 'patients confused about which forms to complete', action: 'headphones', actionLabel: 'Review calls' },
];

export const RCPT_CALENDAR_IMPACT: CalendarImpactData = {
  appointmentsCreated: 18,
  newPatients: 7,
  hygieneRecallConversions: 4,
  sameDayBookings: 3,
  reschedules: 5,
  cancellationsSaved: 2,
  openOpportunities: 3,
  appointmentIntentConversion: 86,
};

export const RCPT_PATIENT_EXPERIENCE: PatientExperienceData = {
  smoothPct: 84,
  neutralPct: 12,
  frustratedPct: 4,
  totalConversations: 64,
  oneLine:
    'Most conversations were smooth today. Friction was mainly around insurance participation and same-day availability.',
  topFrictions: [
    { label: 'Insurance participation', count: 4 },
    { label: 'Same-day availability', count: 3 },
  ],
};

export const RCPT_BUSINESS_IMPACT: BusinessImpactData = {
  productionSupported: 18400,
  period: 'this month',
  methodology: 'Estimated from completed bookings × average production value.',
  drivers: ['new patient bookings', 'recall conversions', 'same-day appointments'],
};
